import logger from './logger';
let deferredPrompt = null;
let installPromptListener = null;

export const Platform = {
  IOS: 'ios',
  INSTALLABLE: 'installable',
  NOT_INSTALLABLE: 'not_installable',
  INSTALLED: 'installed'
};

export function isIOS() {
  if (typeof window === 'undefined') return false;

  const ua = navigator.userAgent;
  const isIPad = /iPad/.test(ua);
  const isIPhone = /iPhone/.test(ua);
  const isIPod = /iPod/.test(ua);

  // iPadOS detection: iPad running iOS 13+ shows as Mac in UA but supports touch
  const isMacOS = /Mac/.test(ua);
  const isTouchCapable = 'ontouchend' in document;
  const isIPadOS = isMacOS && isTouchCapable;

  return isIPad || isIPhone || isIPod || isIPadOS;
}

export function isAndroid() {
  if (typeof window === 'undefined' || !navigator.userAgent) return false;

  const ua = navigator.userAgent;
  const isAndroidDevice = /Android/i.test(ua);
  const isAndroidBrowser = /chrome|chromium|crios|firefox|fxios|samsungbrowser|opera|opr|edge|edg/i.test(ua);

  return isAndroidDevice && isAndroidBrowser && !isIOS();
}

export function isInstalled() {
  if (typeof window === 'undefined') return false;

  // Check if running as a Capacitor native app (window.Capacitor is injected by the bridge)
  const isNative = typeof window.Capacitor === 'object' &&
                   typeof window.Capacitor.isNativePlatform === 'function' &&
                   window.Capacitor.isNativePlatform();

  // Check display-mode media query
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  // Check navigator.standalone for iOS Safari
  const isIOSStandalone = navigator.standalone === true;

  // Check if running as PWA (multiple methods for better detection)
  const isPWA = isNative ||
                isStandalone ||
                window.matchMedia('(display-mode: minimal-ui)').matches ||
                isIOSStandalone ||
                document.referrer.includes('android-app://') ||
                window.location.search.includes('source=pwa');

  return isPWA;
}

export function getInstallState() {
  // Check installed state first
  if (isInstalled()) {
    return Platform.INSTALLED;
  }

  // Check iOS
  if (isIOS()) {
    return Platform.IOS;
  }

  // Check if we have a deferred prompt (works for Android, Windows, macOS)
  if (deferredPrompt) {
    return Platform.INSTALLABLE;
  }

  // Additional check for Android devices that might support PWA
  if (typeof window !== 'undefined' && navigator.userAgent) {
    const ua = navigator.userAgent.toLowerCase();
    const isAndroid = /android/.test(ua);
    const isChrome = /chrome/.test(ua) && !/edg/.test(ua);

    // Android Chrome might support PWA even without beforeinstallprompt
    if (isAndroid && isChrome && window.matchMedia('(display-mode: browser)')) {
      return Platform.INSTALLABLE;
    }
  }

  return Platform.NOT_INSTALLABLE;
}

export function registerBeforeInstallPromptListener(onAvailable) {
  if (typeof window === 'undefined') return () => {};

  const handleBeforeInstallPrompt = (e) => {
    e.preventDefault();
    deferredPrompt = e;
    onAvailable();
  };

  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

  installPromptListener = () => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  };

  return installPromptListener;
}

export async function promptPWAInstall() {
  if (typeof window === 'undefined') return 'unavailable';

  if (!deferredPrompt) {
    return 'unavailable';
  }

  try {
    const result = await deferredPrompt.prompt();
    const outcome = result.outcome;

    if (outcome === 'accepted') {
      deferredPrompt = null;
      return 'accepted';
    } else {
      return 'dismissed';
    }
  } catch (error) {
    logger.error('Error prompting PWA install:', error);
    return 'unavailable';
  }
}

// Analytics helper functions (can be called from components)
export function trackCTAClick(platform) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'cta_get_app_click', {
      platform,
      custom_map: { custom_parameter_1: 'platform' }
    });
  } else {
    logger.debug('[Analytics] CTA click:', { platform });
  }
}

export function trackInstallPromptShown() {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'pwa_install_prompt_shown');
  } else {
    logger.debug('[Analytics] PWA install prompt shown');
  }
}

export function trackInstallAccepted() {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'pwa_install_accepted');
  } else {
    logger.debug('[Analytics] PWA install accepted');
  }
}

export function trackInstallDismissed() {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'pwa_install_dismissed');
  } else {
    logger.debug('[Analytics] PWA install dismissed');
  }
}

export function trackIOSModalOpened() {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'ios_install_modal_opened');
  } else {
    logger.debug('[Analytics] iOS install modal opened');
  }
}

const GOOGLE_PLAY_URL = process.env.REACT_APP_GOOGLE_PLAY_URL || '';

export function getGooglePlayUrl() {
  return GOOGLE_PLAY_URL;
}

export function openGooglePlay() {
  if (!GOOGLE_PLAY_URL) {
    logger.warn('Google Play URL not configured (REACT_APP_GOOGLE_PLAY_URL)');
    return;
  }

  if (typeof window !== 'undefined') {
    window.open(GOOGLE_PLAY_URL, '_blank', 'noopener,noreferrer');
  }
}
